import * as THREE from 'three';
import Experience from '../Experience'
import Plane from '../World/Plane';
import { billboardEls, interactedObjects, channelJourney, upcomingConnections, linesToBillboardEls, existingChannelIDs, existingUserIDs, landingStuff, walkthrough, pathwayItems } from '../World/sets';
import DataFetcher from '../Data/arenafetcher';
import SpriteElement from '../World/SpriteElement';
import {stringify, parse} from 'flatted'
import SpriteText from 'three-spritetext';
import { brown, picnic, primaryColor, secondaryColor, walla, white } from './styles';
import LineElement from '../World/LineElement';
import ConnectionManager from './ConnectionManager';
import { normalizeBlockType } from '../Data/arenaNormalizer';
import type { ExperienceLike } from '../types/core';
import type { ChannelSummary } from '../types/domain';
import type { SceneUserData, UserDataObject } from '../types/scene';
import { debugLog } from './debugLog';
import {
    serializeChannel,
    serializeLineWithPositions,
    serializePlaneBlock,
    serializeTextBlock,
} from './sceneSerialization';

type ApiClientLike = {
    get: (endpoint: string) => Promise<unknown>;
};
type SceneMaterialLike = THREE.Material & { map?: THREE.Texture };
type SceneObjectLike = THREE.Object3D & {
    geometry?: { dispose?: () => void };
    material?: SceneMaterialLike | SceneMaterialLike[];
    userData: UserDataObject;
};
type ChannelLike = Partial<ChannelSummary>;
type SceneIntersectLike = THREE.Object3D & {
    userData: {
        channelData?: ChannelLike;
        connection?: ChannelLike;
    } & UserDataObject;
};
type BlockResult = Plane | SpriteElement;
type PaddingMode = "sprite" | "mesh";

export default class SceneOperations {
    experience!: ExperienceLike
    scene!: THREE.Scene
    camera!: ExperienceLike["camera"]
    connectionManager!: ConnectionManager
    API!: ApiClientLike
    endpoint: string | number | null = null
    dataFetcher!: DataFetcher
    firstClickedChannel = true
    autorotate = true
    titleSpriteElement: THREE.Object3D | null = null
    block: BlockResult | null = null

    private getSceneUserData(scene: THREE.Scene = this.scene): SceneUserData {
        return scene.userData as SceneUserData;
    }

    /** @param {{ endpoint?: string | null, data?: unknown }=} options */
    constructor({ endpoint = null, data = null } = {}) {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.camera = this.experience.camera;
        this.connectionManager = this.experience.connectionManager
        this.API = this.experience.API
        this.endpoint = endpoint;
        this.dataFetcher = this.experience.dataFetcher || new DataFetcher(this.endpoint);
        this.firstClickedChannel = true
        this.autorotate = true
        this.titleSprite()
        this.tagline()
        // this.initLanding()
    }
    disposeSceneObject(object: SceneObjectLike) {
        if (object.geometry) {
            object.geometry.dispose?.();
        }
        if (object.material) {
            if (Array.isArray(object.material)) {
                object.material.forEach((material) => {
                    if (material && typeof material.dispose === 'function') {
                        material.dispose();
                    }
                });
            } else {
                if (typeof object.material.dispose === 'function') {
                    object.material.dispose();
                }
                if (object.material.map) {
                    object.material.map.dispose();
                }
            }
        }
    }
    async initLanding(){
        const taglines = this.scene.children.filter((child)=>{ return child.name.includes("TaglineSprite") })    
        const taglinelines = this.scene.children.filter((child)=>{ return child.name.includes("TaglineLine") })    
        taglines.forEach((tagline)=>{
            const typedTagline = tagline as SceneObjectLike;
            this.scene.remove(tagline)
            this.disposeSceneObject(typedTagline);
        })
        taglinelines.forEach((taglineline)=>{
            const typedTaglineLine = taglineline as SceneObjectLike;
            this.scene.remove(taglineline)
            this.disposeSceneObject(typedTaglineLine);
        })
        this.experience.uiHandlers.wanderBtn.classList.remove("hidden")
        this.autorotate = true
        this.experience.uiHandlers.wakeLandingAlert()
        const wanderSprite = new SpriteElement({title: "wander", role:"Landing", origin: this.titleSpriteElement})
        if (wanderSprite.label?.userData?.instance) {
            wanderSprite.label.userData.instance.changeSpriteColor(wanderSprite.label, secondaryColor, brown)
        }
        const infoSprite = new SpriteElement({title: " ? ", role:"Landing", origin: this.titleSpriteElement})
        await this.connectionManager.positionConnection(wanderSprite.label, this.titleSpriteElement)
        await this.connectionManager.positionConnection(infoSprite.label, this.titleSpriteElement)
        // const pathsSprite = new SpriteElement({title: "paths", role:"Landing", origin: this.titleSpriteElement})
        // const walkthroughSprite = new SpriteElement({title: "walkthrough", role:"Landing", origin: this.titleSpriteElement, slug:"walkthrough"})
        // await this.connectionManager.positionConnection(pathsSprite.label, this.titleSpriteElement)
        // await this.connectionManager.positionConnection(walkthroughSprite.label, this.titleSpriteElement)
        const landingLines = [
            new LineElement(this.titleSpriteElement, wanderSprite.label),
            new LineElement(this.titleSpriteElement, infoSprite.label ),
            // new LineElement(this.titleSpriteElement, pathsSprite.label ),
            // new LineElement(this.titleSpriteElement, walkthroughSprite.label )
        ]
        landingStuff.push(wanderSprite.label, /* pathsSprite.label */ infoSprite.label, /* walkthroughSprite.label */)
        landingLines.forEach((line)=>{
            landingStuff.push(line.line)
        })
    }
    async initWalkthrough(){
        
        // this.autorotate = false
        // console.log("initWalkthrough");
        // const walkthroughSprite = await this.scene.getObjectByName("walkthroughSprite")
        // const introSprite = new SpriteElement({
        //     title: "Pathways is a 3D Exploration Space, taking networked media for a walk, in a digital garden of forking paths. \n\nSave your path, for you or fellow wanderers. Fork new paths, trace new trajectories.", 
        //     role:"Walkthrough", 
        //     origin: walkthroughSprite, 
        //     slug:"intro"})
        // const startPathsSprite = new SpriteElement({
        //     title: "Start your media promenade by clicking a trailhead – an initial media block you either choose or randomize. \n\nOr explore the paths index and fork someone else's public path, to pursue its potential trajectories", 
        //     role:"Walkthrough", 
        //     origin: introSprite.label, 
        //     slug:"startpaths"})
        // const crossroadsPathsSprite = new SpriteElement({
        //     title: "Connected to this clicked trailhead lies crossroads – neighborhing interconnected media from the Are.na database. \n\nThese crossroads, either containers or media, assembles collective spheres of interest to dive in. \n\nClicking a crossroad will turn it into a gateway, propulsing you towards it, in order to activate and explore further available trajectories.", 
        //     role:"Walkthrough", 
        //     origin: startPathsSprite.label, 
        //     slug:"crossroads"})
        // const pathwaySprite = new SpriteElement({
        //     title: "Through each of your interacted crossroads gateways, your journey and its landscape progress. This is how your pathway unravels, in which a path traces your desire line – one crossroad at a time. \n\nIf hovered, can filter out unvisited crossroads for better overwatch. \n\nA second path traces your movement around the landscape, documenting presence through shared pathways.", 
        //     role:"Walkthrough", 
        //     origin: crossroadsPathsSprite.label, 
        //     slug:"pathway"})
        // const saveSprite = new SpriteElement({
        //     title: "Save your path privately, for you to revisit later on.\n\nor publicly, for others to fork paths from yours, and walk in your footsteps.",
        //     role:"Walkthrough", 
        //     origin: pathwaySprite.label, 
        //     slug:"save"})
        // const contextMenuSprite = new SpriteElement({
        //     title: "Right click any media will allow you to access a context menu, where you can access its source, copy its id to restart a path from it.",
        //     role:"Walkthrough", 
        //     origin: saveSprite.label, 
        //     slug:"contextmenu"})
        // const overwatchSprite = new SpriteElement({
        //     title: "Take a step back and overwatch your pathway, by hovering activated crossroads. \n\nActivate autoplay and/or use the arrow keys to navigate your path. \n\nUse the space bar to overwatch and zoom back to where you can go next.",
        //     role:"Walkthrough", 
        //     origin: contextMenuSprite.label, 
        //     slug:"overwatch"})
        // const moodSprite = new SpriteElement({
        //     title: "Set your journey to a any preferred time of the day, be it day, night, dawn or dusk, to set the mood of your pathway.",
        //     role:"Walkthrough", 
        //     origin: overwatchSprite.label, 
        //     slug:"mood"})
        // const snapshotSprite = new SpriteElement({
        //     title: "Gazing through the landscape, you can take a snapshot of your pathway anytime.",
        //     role:"Walkthrough", 
        //     origin: moodSprite.label, 
        //     slug:"snapshot"})
        // const endWalkthroughSprite = new SpriteElement({
        //     title: "Ready? Click me to bring you back to the main screen.",
        //     role:"Walkthrough", 
        //     origin: snapshotSprite.label, 
        //     slug:"endWalkthrough"})
        // await this.connectionManager.positionConnection(introSprite.label, walkthroughSprite, 15)
        // await this.connectionManager.positionConnection(startPathsSprite.label, introSprite.label, 30)
        // await this.connectionManager.positionConnection(crossroadsPathsSprite.label, startPathsSprite.label, 30)
        // await this.connectionManager.positionConnection(pathwaySprite.label, crossroadsPathsSprite.label, 30)
        // await this.connectionManager.positionConnection(saveSprite.label, pathwaySprite.label, 30)
        // await this.connectionManager.positionConnection(contextMenuSprite.label, saveSprite.label, 30)
        // await this.connectionManager.positionConnection(overwatchSprite.label, contextMenuSprite.label, 30)
        // await this.connectionManager.positionConnection(moodSprite.label, overwatchSprite.label, 30)
        // await this.connectionManager.positionConnection(snapshotSprite.label, moodSprite.label, 30)
        // await this.connectionManager.positionConnection(endWalkthroughSprite.label, snapshotSprite.label, 30)
        // walkthrough.push(introSprite, startPathsSprite, crossroadsPathsSprite, pathwaySprite, saveSprite, contextMenuSprite, overwatchSprite, moodSprite, snapshotSprite, endWalkthroughSprite)
        // const walkthroughLines = [
        //     new LineElement(walkthroughSprite, introSprite.label),
        //     new LineElement(introSprite.label, startPathsSprite.label ),
        //     new LineElement(startPathsSprite.label, crossroadsPathsSprite.label ),
        //     new LineElement(crossroadsPathsSprite.label, pathwaySprite.label ),
        //     new LineElement(pathwaySprite.label, saveSprite.label ),
        //     new LineElement(saveSprite.label, contextMenuSprite.label ),
        //     new LineElement(contextMenuSprite.label, overwatchSprite.label ),
        //     new LineElement(overwatchSprite.label, moodSprite.label ),
        //     new LineElement(moodSprite.label, snapshotSprite.label ),
        //     new LineElement(snapshotSprite.label, endWalkthroughSprite.label ),
        // ]
        // const boundingBox = new THREE.Box3();
        // new Promise((resolve) => {
        //     this.scene.traverseVisible((visibleEl) => {
        //         if (["intro", /*"startpaths",  "crossroads", "pathway", "save", "contextmenu", "overwatch", "mood", "snapshot" */].includes(visibleEl.slug)) {
        //             const ObjBoundingBox = new THREE.Box3().setFromObject(visibleEl);
        //             boundingBox.expandByPoint(ObjBoundingBox.min);
        //             boundingBox.expandByPoint(ObjBoundingBox.max);
        //         }
        //     });
        //     resolve();
        // });
        
        // this.camera.getCameraControls().fitToBox(
        //     boundingBox, true, {
        //     paddingLeft: 1,
        //     paddingRight: 1,
        //     paddingBottom: 1,
        //     paddingTop: 1,
        // })
    }
    async initWander(intersect: THREE.Object3D){
        const randomSprite = new SpriteElement({title: "anywhere", role:"Landing"})
        const idSprite = new SpriteElement({title: "somewhere", role:"Landing"})
        await this.connectionManager.positionConnection(randomSprite.label, intersect)
        await this.connectionManager.positionConnection(idSprite.label, intersect)
        const wanderSprite = this.scene.getObjectByName("wanderSprite")
        const randomLine = new LineElement(wanderSprite, randomSprite.label)
        const idLine = new LineElement(wanderSprite, idSprite.label)
        landingStuff.push(randomSprite.label, idSprite.label, randomLine.line, idLine.line)
        const boundingBox = new THREE.Box3();
        this.scene.traverseVisible((visibleEl) => {
            if (["wanderSprite", "somewhereSprite", "anywhereSprite"].includes(visibleEl.name)) {
                const ObjBoundingBox = new THREE.Box3().setFromObject(visibleEl);
                boundingBox.expandByPoint(ObjBoundingBox.min);
                boundingBox.expandByPoint(ObjBoundingBox.max);
            }
        });
        await this.fitToBoxWithPadding(boundingBox, "sprite")
    }
    async tagline(){
        const center = new THREE.Object3D
        const taglineSprites: SpriteText[] = []
        const words = ["Taking networked media for a walk", "through a digital garden", "of forking paths"/* , "paths", "paths", "paths" */]
        let previousTagline: SpriteText | null = null;
        let ofForkingTagline: SpriteText | null = null;
        const direction = new THREE.Vector3(.25, 0, 0); // Example direction vector (along the x-axis)
        const distance = 1; // Distance between taglines
    
        for (let index = 0; index < words.length; index++) {
            const word = words[index];
            const tagline = new SpriteText(word, .2, brown);

            if (previousTagline) {
                // Calculate the new position based on the previous tagline's position and the direction vector
                const newPosition = previousTagline.position.clone().add(direction.clone().multiplyScalar(distance));
                tagline.position.set(newPosition.x, newPosition.y, newPosition.z);
                tagline.position.x += (Math.random() - 0.5) * 2;
                tagline.position.y += (Math.random() - 0.5) * 2;
                tagline.position.z += (Math.random() - 0.5) * 2;
            } else {
                // Position the first tagline at the center
                tagline.position.set(center.position.x-2, center.position.y, center.position.z+1);
                tagline.position.x += (Math.random() - 0.5) * 1;
                tagline.position.y += (Math.random() - 0.5) * 1;
                tagline.position.z += (Math.random() - 0.5) * 1;
            }
            // await this.connectionManager.positionConnection(tagline, center, 4);
            tagline.fontFace = picnic;
            tagline.fontSize = 100;
            tagline.name = "TaglineSprite" + index;
            // console.log(tagline);
            this.scene.add(tagline);
            landingStuff.push(tagline);
            taglineSprites.push(tagline);
            // if (word === "of forking paths") {
            //     ofForkingTagline = tagline;
            // }
            if (previousTagline && !(word === "paths" && index >= 4)) {
                const line = new LineElement(previousTagline, tagline);
                if (line.line) {
                    line.line.name = "TaglineLine" + index;
                }
            }
            previousTagline = tagline;
        }
        // for (let index = 4; index < words.length; index++) {
        //     const tagline = taglineSprites[index];
        //     if (ofForkingTagline && tagline) {
        //         const line = new LineElement(ofForkingTagline, tagline);
        //         line.line.name = "TaglineLine" + index;
        //     }
        // }
    }
    async titleSprite(){
        const titleSprite = new SpriteText("Pathways", 1, primaryColor)
        titleSprite.fontFace = picnic
        titleSprite.fontSize = 200
        titleSprite.name = "TitleSprite"
        this.scene.add(titleSprite)
        this.titleSpriteElement = titleSprite
        landingStuff.push(titleSprite)

        return titleSprite
        // await this.experience.resources.loaders.SVGLoader.load(
        //     './Pathways-logo.svg', 
        //     (data)=>{
        //         console.log(data);
        //         const paths = data.paths
        //         const titleSprite = new THREE.Group()
        //         for(let i = 0; i<paths.length;i++){
        //             const path = paths [i]
        //             const material = new THREE.MeshBasicMaterial({
        //                 color: primaryColor,
        //                 side: THREE.DoubleSide,
        //                 depthWrite: false,
        //             })
        //             const shapes = SVGLoader.createShapes(path)
        //             for(let j=0; j<shapes.length; j++){
        //                 const shape = shapes [j]
        //                 const geometry = new THREE.ShapeGeometry(shape)
        //                 const mesh = new THREE.Mesh(geometry, material)
        //                 titleSprite.add(Mesh)

        //             }
        //         }
        //         this.scene.add(titleSprite)
        //         this.titleSpriteElement = titleSprite
        //         landingStuff.push(titleSprite)
        //         console.log(titleSprite);
        //         return titleSprite
        //     },
        //     function ( xhr ) {
        //         console.log( ( xhr.loaded / xhr.total * 100 ) + '% loaded' );
        //     },
        //     // called when loading has errors
        //     function ( error ) {
        //         console.log( 'An error happened' );
        //     }
        // )
    }
    resetAllSets(){
        billboardEls.clear()
        interactedObjects.length = 0
        upcomingConnections.clear()
        channelJourney.length=0
        existingChannelIDs.clear()
        linesToBillboardEls.clear()
        existingUserIDs.clear()
        landingStuff.length = 0
        pathwayItems.length = 0

    }
    async loadRandomBlock(_scene: THREE.Scene) {
        if(!this.experience.uiHandlers.restartAnywhereElseBtn.classList.contains("active")){
            this.experience.uiHandlers.restartAnywhereElseBtn.classList.add("active")
        }
        await this.experience.exportPath.createNewSceneEntry(this.experience.scene.userData, "");
        this.autorotate = false
        // Implement the random block loading functionality here
            try {
                const plane = new Plane({ endpoint: "/search?query=*&sort=random&type=Image&per=1" });
                await plane.fetchAndCreate();
                if(plane.mesh instanceof THREE.Object3D){
                    billboardEls.add(plane.mesh)
                    interactedObjects.push(plane.mesh)
                    plane.addToScene()
                    // this.plane = plane; // Store the plane instance
                } else {
                    console.error("Plane mesh is not a valid THREE.Object3D:", plane.mesh);
                }
            } catch (error) {
                console.error("Error creating initial plane:", error);
            }
    }
    /** @param {string | number} blockID */
    async loadBlockID(blockID: string | number)
    {      
        await this.experience.exportPath.createNewSceneEntry(this.experience.scene.userData, "");     
        this.autorotate = false
        try{
            this.endpoint = blockID
            this.dataFetcher = new DataFetcher(`/blocks/${this.endpoint}`);
            const data = await this.dataFetcher.fetchData();
            if (!data) {
                throw new Error(`No data found for blockID ${blockID}`);
            }
            let block: BlockResult | null = null;
            switch(normalizeBlockType(data))
            {
                case "Image":
                case "Attachment":
                case "Media":
                case "Link":
                    block = new Plane({ data: data });
                    await block.create()
                    if (block.mesh) {
                        this.scene.add(block.mesh);
                    }
                    break;
                case "Text":
                    block = new SpriteElement(data)

                    // await block.create()
                    break;
                default:
                    console.warn("Unknown data class", data.class);
                    return;
            }
            // console.log(block.mesh, block.label);
            if(block instanceof Plane && block.mesh){
                billboardEls.add(block.mesh)
                interactedObjects.push(block.mesh)
                await this.experience.sceneUtils.createDataLabels(block.mesh)
                this.block = block; // Store the block instance
            } 
            else if(block instanceof SpriteElement && block.label){
            //     console.log("hi");
            //     interactedObjects.push(block.root)
                this.block = block;
            // } else {
            //     console.error("mesh is not a valid THREE.Object3D:", block.mesh);
            }
        } catch (error) {
            console.error("Error loading block by ID:", error);
        }

    }
    setSceneFirstName(intersect: SceneIntersectLike)
    {
        const userData = this.getSceneUserData();
        if(this.firstClickedChannel){
            // console.log("setSceneFirstName", this.firstClickedChannel);
          if(intersect.userData.channelData){
            userData.trailHead = `${intersect.userData.channelData.title}`;
          } else if (intersect.userData.connection){
            userData.trailHead = `${intersect.userData.connection.title}`;
          }
          this.firstClickedChannel = false;
          debugLog(userData);
          const data = intersect.userData.channelData || intersect.userData.connection
          if (this.isChannelSummary(data)) {
            this.experience.uiHandlers.updatePathwayTitle(data)
          }
        }
      }
    setSceneLastName(intersect: SceneIntersectLike)
    {
        const userData = this.getSceneUserData();
        // console.log(intersect);
        const lastChannelName = intersect.userData.channelData?.title || intersect.userData.connection?.title || ""
        if(lastChannelName !== userData.trailHead){
            debugLog("setSceneLastName", lastChannelName, userData);
            userData.trailEnd = `${lastChannelName}`
            const data = intersect.userData.channelData || intersect.userData.connection
            if (this.isChannelSummary(data)) {
                this.experience.uiHandlers.updatePathwayTitle(data)
            }
            this.setSceneFullName()
        }
    }
    setSceneFullName()
    {
        const userData = this.getSceneUserData();
        if(userData.trailHead && userData.trailEnd){
            // console.log("setSceneFullName");
            userData.name = `From ${userData.trailHead} to ${userData.trailEnd}`
            userData.slug = `${userData.name.toLowerCase().replace(/\s/g, "-")}`
            this.scene.name = userData.name
        }
    }
    /** @param {HTMLElement} pagePathwayTitle */
    setSceneTrailJourney (pagePathwayTitle: HTMLElement) 
    {
        debugLog("setSceneTrailJourney");
        const userData = this.getSceneUserData();
        if(pagePathwayTitle.innerHTML !== ""){
          userData.journey = channelJourney
          //need to trim
        }
    }
    setWanderer(name: string, scene: THREE.Scene){
        const userData = this.getSceneUserData(scene);
        if(!userData.wanderer){
            userData.wanderer = []
        }
        userData.wanderer.push(name)
        debugLog(userData.wanderer);
    }
    async collectVisibleObjectsBoundingBox(boundingBox: THREE.Box3) {
        this.scene.traverseVisible((visibleEl) => {
            if (["Object3D", "Mesh", "Sprite", "Group" ].includes(visibleEl.type) && !visibleEl.name.includes("camLineGroup")) {
                const ObjBoundingBox = new THREE.Box3().setFromObject(visibleEl);
                boundingBox.expandByPoint(ObjBoundingBox.min);
                boundingBox.expandByPoint(ObjBoundingBox.max);
            }
        });
    }
    async collectUpcomingConnectionsBoundingBox(boundingBox: THREE.Box3) {
        this.scene.traverseVisible((visibleEl) => {
            if (["Object3D", "Mesh", "Line", "Line2", "Sprite"].includes(visibleEl.type) && upcomingConnections.has(visibleEl)) {
                const ObjBoundingBox = new THREE.Box3().setFromObject(visibleEl);
                boundingBox.expandByPoint(ObjBoundingBox.min);
                boundingBox.expandByPoint(ObjBoundingBox.max);
            }
        });
    }
    emptyScene(scene: THREE.Scene) {
        // console.log("empty clicked");
        if (this.experience.saveTimeout) {
            clearTimeout(this.experience.saveTimeout);
            this.experience.saveTimeout = null;
        }
        if(this.experience.sceneDocId !== null){
            this.experience.sceneDocId = null
            debugLog(this.experience.sceneDocId);
        }
        if(this.experience.cameraPathTracker !== null){
            this.experience.cameraPathTracker.destroy()
        }
        // Check if the scene object is valid
        if (!this.scene || !this.scene.traverse) {
            console.error('Invalid scene object passed to emptyScene.');
            return;
        }
        if(scene?.userData){
            scene.userData = {}
        }
        // Function to remove meshes, lines, and sprites
        const removeObjects = (object: SceneObjectLike) => {
            if(object.type==="Group" || object.type==="Line" || object.type==="Line2"  || object.type==="Mesh" || object.type === "Sprite" || object.type === "Object3D"){
                object.parent?.remove(object);
                this.disposeSceneObject(object);
            }  
        };
        const traverseAndRemove = (object: THREE.Object3D) => {
            object.children.forEach((child) => {
                removeObjects(child as SceneObjectLike);
                if(child.type==="Object3D" && child.children.length > 0){
                    traverseAndRemove(child);
                }
            })
        }
        while(scene.children.length > 1){
            traverseAndRemove(scene);
        }
        // console.log(this.scene, this.experience.resources.loaders.textureLoader);
    }
    async cloneVisibleObjectsToTempScene(tempScene: THREE.Scene) {
        const tempUserData = this.getSceneUserData(tempScene);
        tempUserData.elementsToRegenerate = {
            cameraLines:{},
            crossroadLines:{},
            pathLines:{},
            textBlocks:{},
            channels:{},
            planeBlocks:{},
        }
        this.scene.traverseVisible((visibleEl) => {
            // line2 not supported on export TODO
            if(["Line", "Line2"].includes(visibleEl.type)){
                if(visibleEl.name === "CameraLine"){
                    // console.log("CameraLine", visibleEl);
                    tempUserData.elementsToRegenerate!.cameraLines![visibleEl.uuid] = {
                        name: visibleEl.name,
                        points: visibleEl.userData.points
                    }
                }
                if(visibleEl.name === "CrossroadLine"){
                    // console.log("CrossroadLine", visibleEl);
                    tempUserData.elementsToRegenerate!.crossroadLines![visibleEl.uuid] =
                            serializeLineWithPositions(visibleEl);
                }
                if(visibleEl.name === "PathLine"){
                        tempUserData.elementsToRegenerate!.pathLines![visibleEl.uuid] =
                                serializeLineWithPositions(visibleEl);
                }
            } else 
            if (["Object3D"].includes(visibleEl.type)) { 
                if(visibleEl.name === "TextBlock"){
                    tempUserData.elementsToRegenerate!.textBlocks![visibleEl.uuid] =
                            serializeTextBlock(visibleEl);
                } else if(visibleEl.name === "Channel"){
                    debugLog("Channel", visibleEl);
                    tempUserData.elementsToRegenerate!.channels![visibleEl.uuid] =
                            serializeChannel(visibleEl);

                } else {
                    debugLog("not channel/textblock el", visibleEl);
                }

            } else
            if(["Mesh"].includes(visibleEl.type)){
                tempUserData.elementsToRegenerate!.planeBlocks![visibleEl.uuid] =
                    serializePlaneBlock(visibleEl as THREE.Object3D & { userData: UserDataObject });
                // try{
                //     const clone = visibleEl.clone(true);
                //     tempScene.add(clone);

                // } catch {
                //     console.error("Error cloning element:", visibleEl, error);
                // }
            }
        });
    }
    
    async saveSceneState(){
        debugLog("[pathways] saveSceneState started", {
            sceneDocId: this.experience.sceneDocId,
            sceneName: this.scene.name,
        });
        this.experience.pathview.filterPath(null);
        // this.checkScene() // for circular data (anything on quaternion, scale, parent scene ok)
        const boundingBox = new THREE.Box3();
        const tempScene = new THREE.Scene();
        tempScene.userData = this.getSceneUserData();
        await this.collectVisibleObjectsBoundingBox(boundingBox);
        await this.cloneVisibleObjectsToTempScene(tempScene);
        await this.fitCameraToBoundingBox(boundingBox)
        debugLog("temp data", tempScene);    
        debugLog("[pathways] exporting scene", {
            hasElementsToRegenerate: Boolean(this.getSceneUserData(tempScene).elementsToRegenerate),
        });
        await this.experience.exportPath.exportGLTF(tempScene);
        // await this.experience.pathview.removeFilter();
    }
    // async takeScreenshot(canvas, sceneData)
    // {
    //     if (canvas && sceneData) {
    //         this.experience.update()
    //         await canvas.toBlob(async (blob) => {
    //             const screenshotFileName = `${sceneData.slug}-${canvas.width}x${canvas.height}.jpg`;
    //             this.experience.exportPath.downloadScreenshot(blob, screenshotFileName)
    //         });
    //     } else {
    //         console.error('Canvas element not found');
    //     }
    // }
    checkScene(){
           // Utility function to check for circular references
        function isCircular(obj: unknown, seen = new WeakSet<object>()) {
            if (obj && typeof obj === 'object') {
                if (seen.has(obj)) {
                    return true;
                }
                seen.add(obj);
                for (const key in obj) {
                    if (Object.prototype.hasOwnProperty.call(obj, key) && isCircular((obj as UserDataObject)[key], seen)) {
                        return true;
                    }
                }
                seen.delete(obj);
            }
            return false;
        }
        // Function to inspect scene objects
        function inspectScene(scene: THREE.Scene) {
            scene.traverse((object) => {
                debugLog(`Inspecting object: ${object.type}`);
                // Check if the object itself is null or undefined
                if (object == null) {
                    console.warn(`Object is ${object}:`, object);
                }
                // Check each property of the object
                for (const key in object) {
                    if (Object.prototype.hasOwnProperty.call(object, key)) {
                        const value = (object as unknown as UserDataObject)[key];
                        // Check if the property is null or undefined
                        if (value === null || value === undefined) {
                            console.warn(`Property '${key}' is ${value} on object:`, object);
                        }
                        // Check for circular references
                        if (isCircular(value)) {
                            console.warn(`Circular reference found in property '${key}' on object:`, object);
                        }
                    }
                }
            });
        }
        // Inspect the scene for issues
        inspectScene(this.scene);
        // // Now attempt to serialize the scene
        // try {
        //     console.log(this.scene.toJSON());
        // } catch (error) {
        //     console.error('Error serializing scene:', error);
        // }
    }
    /** @param {THREE.Box3} boundingBox */
    async fitCameraToBoundingBox(boundingBox: THREE.Box3) {
        await this.fitToBoxWithPadding(boundingBox, "sprite");
    }
    async fitCameraToUpcomingConnections() {
        const upcomingBox = new THREE.Box3();
        try {
            await this.collectUpcomingConnectionsBoundingBox(upcomingBox);
            // console.log("Collected bounding box:", upcomingBox);
    
            if (upcomingBox.isEmpty()) {
                // console.warn("Bounding box is empty");
            } else {
                this.fitCameraToBoundingBox(upcomingBox);
            }
        } catch (error) {
            console.error("Error collecting or fitting bounding box:", error);
        }
    }
    private isChannelSummary(data: unknown): data is ChannelSummary {
        if (!data || typeof data !== "object") return false;
        const candidate = data as { title?: unknown; id?: unknown; type?: unknown };
        return (
            typeof candidate.title === "string" &&
            typeof candidate.id === "number" &&
            candidate.type === "Channel"
        );
    }
    async fitToBoxWithPadding(target: THREE.Object3D | THREE.Box3, mode: PaddingMode = "sprite", multiplier = 1) {
        const basePadding = mode === "mesh" ? this.camera.paddingMesh : this.camera.paddingSprite;
        const padding = basePadding * multiplier;
        await this.camera.getCameraControls().fitToBox(target, true, {
            paddingLeft: padding,
            paddingRight: padding,
            paddingBottom: padding,
            paddingTop: padding,
        });
    }
}
